import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSendingInput } from "../../../../common/ChatComponents/MessageSendingInput";
import { CollapsedButton } from "../../../../common/ChatComponents/MessageSendingInput/Actions/CollapsedButton";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { AppMarkdownParser } from "../../../../shared/components/AppMarkdownParser";
import { useResearchInput } from "../../hooks/use-research-input";
import { selectCitation } from "../../redux/selectors";
import { CitationsList } from "./CitationsList";
import { ResultInputActions } from "./ResultInputActions";
import { useStorageSelectors } from "../../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { useResearchPolling } from "../../hooks/use-research-polling";
import { ResultHeader } from "./ResultHeader";
import { useAppModals } from "../../../../app/state/modalSlice/hook/use-app-modals";
import { FileTransferModal } from "../../../Modals/FileTransferModal";
import { useFileTransferModal } from "../../../Modals/FileTransferModal/hook/use-file-transfer-modal";
import styles from "./styles.module.css";

export const Result = () => {
  const {
    input,
    handleChangeInput,
    handleSendTask,
    handleSendTaskWithKeyDown,
  } = useResearchInput();
  const { selectedObject } = useStorageSelectors("research");
  const citations = useAppSelector(selectCitation);
  const { handleOpenModal, handleCloseModal } = useAppModals();
  const markdownRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const { handleClick } = useFileTransferModal({
    name: `${selectedObject?.friendly_name ?? ""}.md`,
    content: selectedObject?.value?.research_object?.result?.content,
  });
  const { t } = useTranslation();

  const result = useMemo(() => {
    return selectedObject?.value?.research_object?.result?.content;
  }, [selectedObject?.id, selectedObject?.value?.research_object]);

  useEffect(() => {
    if (markdownRef.current) {
      markdownRef.current.scrollTop = 0;
    }
  }, [result]);

  const handleCollapsedInput = () => {
    setCollapsed((prev) => !prev);
  };

  useResearchPolling();

  return (
    <div className={classNames(styles.container)}>
      <ResultHeader
        selectedObject={selectedObject}
        markdownRef={markdownRef}
        onDownload={() => handleOpenModal("file_transfer_modal", null)}
        isDisabledBtn={!result}
      />
      <div ref={markdownRef} className={styles["result-content"]}>
        <AppMarkdownParser content={result ?? ""} />
        {result && <CitationsList citations={citations ?? []} />}
      </div>
      <div
        className={classNames(
          styles["input-wrapper"],
          collapsed && styles["input-wrapper-collapsed"],
        )}
      >
        <MessageSendingInput
          sendValue={input}
          onChange={handleChangeInput}
          rows={1}
          handleKeyDown={handleSendTaskWithKeyDown}
          actions={
            <ResultInputActions
              handleSendTask={() => {
                handleSendTask();
                setCollapsed(true);
              }}
              handleCollapsedInput={handleCollapsedInput}
              isDisabled={
                selectedObject?.value?.research_object?.run_id !== null
              }
            />
          }
          inputStyle={"elevated"}
          inputPlaceholder={t("research.input_placeholder")}
          isDisabled={
            selectedObject?.value?.research_object?.run_id !== null ||
            !selectedObject?.write
          }
          className={classNames(
            styles["task-input"],
            collapsed && styles["collapsed-input"],
          )}
        />
        {collapsed && (
          <CollapsedButton
            handleCollapsedInput={handleCollapsedInput}
            className={styles["expand-button"]}
          />
        )}
      </div>
      <FileTransferModal
        closeModal={handleCloseModal}
        handleActions={handleClick}
      />
    </div>
  );
};
